"use client";

import useBridgeControls from "@/features/bridge/hooks/useBridgeControls";
import useContractMethods from "@/features/bridge/hooks/useContractMethods.hook";
import { useGeneralAssetsMethods } from "@/features/bridge/hooks/useGeneralAssetMethods";
import useIntegrationManager from "@/features/bridge/hooks/useIntegrationManager.hook";
import useValidation from "@/features/bridge/hooks/useValidation";
import React, { createContext, useContext } from "react";

export type BridgeControlsType = ReturnType<typeof useBridgeControls>;
export type ValidationManagerType = ReturnType<typeof useValidation>;
export type ContractManagerType = ReturnType<typeof useContractMethods>;
export type IntegrationManagerType = ReturnType<typeof useIntegrationManager>;
export type GeneralAssetMethodsType = ReturnType<
  typeof useGeneralAssetsMethods
>;

export interface BridgeContextType {
  bridgeControls: BridgeControlsType;
  validationManager: ValidationManagerType;
  contractManager: ContractManagerType;
  integrationManager: IntegrationManagerType;
  generalAssetMethods: GeneralAssetMethodsType;
}

export const BridgeContext = createContext<BridgeContextType>(
  {} as BridgeContextType,
);

//? BRIDGE CONTEXT
/**
 * Provides context for managing bridge operations, validation, and contract methods.
 *
 * This component initializes and provides context values for bridge controls, validation, and contract
 * management to its child components. It uses custom hooks to manage the state and methods required for
 * bridging operations.
 *
 * @component
 * @param {React.ReactNode} props.children - The child components that will consume the provided context.
 * @returns {JSX.Element} A `BridgeContext.Provider` component that wraps its children with context values.
 */
const BridgeProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  // Manages the rest of the bridge
  const bridgeControls = useBridgeControls();

  // Manages the validation
  const validationManager = useValidation(bridgeControls);

  const generalAssetMethods = useGeneralAssetsMethods(bridgeControls);

  // Manages the method calls
  const contractManager = useContractMethods(
    bridgeControls,
    validationManager,
    generalAssetMethods,
  );

  // Manages the test integration
  const integrationManager = useIntegrationManager(
    bridgeControls,
    contractManager,
  );

  return (
    <BridgeContext.Provider
      value={{
        bridgeControls,
        validationManager,
        contractManager,
        integrationManager,
        generalAssetMethods,
      }}
    >
      {children}
    </BridgeContext.Provider>
  );
};

//? USEBRIDGE HOOK
/**
 * Custom hook to access the `BridgeContext` value.
 *
 * This hook provides access to the context value of `BridgeContext`, which includes bridge controls,
 * validation management, and contract methods. It throws an error if used outside of a `BridgeProvider` context.
 *
 * @hook
 * @returns {BridgeContextType} The context value provided by the `BridgeProvider`.
 * @throws {Error} Throws an error if `BridgeContext` is used outside of a `BridgeProvider`.
 */
export const useBridge = (): BridgeContextType => {
  const bridgeContext = useContext(BridgeContext);
  if (bridgeContext === undefined) {
    throw new Error("BridgeContext must be used within the BridgeProvider!");
  }
  return bridgeContext as BridgeContextType;
};

export default BridgeProvider;
